import { startTransition, useEffect, useEffectEvent, useMemo, useState } from "react";
import { Form, Nav, Tab } from "react-bootstrap";
import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { AnalysisProgrammingCodeTemplate, DocumentReference } from "../ars_ldm";
import { useArsContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { isAnalysisMethod } from "../typeGuards/isAnalysisMethod";
import { AnalysisMethodOperationsControl } from "./AnalysisMethodOperationsControl";
import { AnalysisMethodParametersControl } from "./AnalysisMethodParametersControl";
import { AnalysisProgrammingCodeTemplateControl } from "./AnalysisProgrammingCodeTemplateControl";
import { DocumentReferenceControl } from "./DocumentReferenceControl";
import { NamedObjectControl } from "./NamedObjectControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the AnalysisMethodControl component.
 */
export interface AnalysisMethodControlProps {
  /** RFC 6901 JSON Pointer path to the AnalysisMethod in ArsContext state. */
  dataPath: string;
  /** Can rename the object. */
  canRename?: boolean;
  /** Disabled state. */
  disabled?: boolean;
  /** Editing via the TLF */
  inTLF?: boolean;
}

/**
 * A component for managing AnalysisMethod entities with nested operations.
 *
 * This component provides:
 * - Properties tab for editing named object fields
 * - Dynamic operation tabs for managing method operations
 * - Add operation action
 * - Optional remove button for deleting the analysis method
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the AnalysisMethod in ArsContext state.
 * @param props.canRename - Optional flag to allow renaming and removing the object.
 * @param props.disabled - Optional flag to disable editing controls.
 * @param props.inTLF - Optional flag indicating if editing is via the TLF.
 * @returns A React functional component rendering the analysis method editor.
 */
export const AnalysisMethodControl = ({
  dataPath,
  canRename = false,
  disabled = false,
}: AnalysisMethodControlProps): React.ReactElement => {
  const { state, dispatch } = useArsContext();
  const [activeTab, setActiveTab] = useState<string>("named-object");

  const analysisMethod = getFromDataPath(dataPath, state, isAnalysisMethod);
  const documentReferences: DocumentReference[] = useMemo(
    () => analysisMethod?.documentRefs ?? [],
    [analysisMethod],
  );
  const operations = useMemo(() => analysisMethod?.operations ?? [], [analysisMethod]);
  const codeTemplate = analysisMethod?.codeTemplate;
  const hasCodeTemplate = Boolean(
    codeTemplate?.context ||
    codeTemplate?.code ||
    codeTemplate?.documentRef ||
    codeTemplate?.parameters?.length,
  );
  // Disable editing for parent templates
  if (analysisMethod?.disableEditing) disabled = true;
  // Check state settings for disableAnalysisMethodEdit
  if (state.disableAnalysisMethodEdit) disabled = true;

  /**
   * Event handler to update the active tab.
   *
   * @param newTab - Tab key to activate.
   */
  const updateActiveTab = useEffectEvent((newTab: string) =>
    startTransition(() => setActiveTab(newTab)),
  );

  /**
   * Change the active document tab if it no longer exists.
   */
  useEffect(() => {
    if (
      activeTab.startsWith("document-references-") &&
      documentReferences.length > 0 &&
      parseInt(activeTab.split("-")[2], 10) >= documentReferences.length
    ) {
      updateActiveTab(`document-references-${documentReferences.length - 1}`);
    } else if (activeTab.startsWith("document-references-") && documentReferences.length === 0) {
      updateActiveTab(`named-object`);
    }
  }, [operations.length, documentReferences.length, activeTab]);

  /**
   * Handles adding a new document reference to the method.
   */
  const handleAddDocumentReference = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && analysisMethod) {
      const documentReferenceCount = documentReferences.length;
      const newDocumentReference: DocumentReference = {
        referenceDocumentId: "",
        pageRefs: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/documentRefs/${documentReferenceCount}`,
        value: newDocumentReference,
      });
    }
  };

  /**
   * Handles adding a new programming code template to the method.
   */
  const handleAddCodeTemplate = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && analysisMethod && !hasCodeTemplate) {
      const newCodeTemplate: AnalysisProgrammingCodeTemplate = {
        context: "",
        code: "",
        parameters: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/codeTemplate`,
        value: newCodeTemplate,
      });
    }
  };

  const idRoute = dataPath.replace(/\//g, "-");

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      {analysisMethod && (
        <div className="position-absolute bottom-0 end-0 m-1">
          <Form.Text>
            <small>Id: {dataPath.split("/").pop()}</small>
          </Form.Text>
        </div>
      )}
      {canRename && (
        <div className="position-absolute top-0 end-0 m-2">
          <RemoveArsItemButton
            dataPath={dataPath}
            disabled={disabled}
          />
        </div>
      )}

      <Tab.Container
        activeKey={activeTab}
        onSelect={(k) => {
          /* istanbul ignore else */
          if (k) {
            setActiveTab(k);
          }
        }}
      >
        <Nav
          variant="tabs"
          className="mb-3"
        >
          <Nav.Item>
            <Nav.Link eventKey="named-object">Properties</Nav.Link>
          </Nav.Item>
          {documentReferences.length > 0 &&
            documentReferences.map((documentReference, index) => (
              <Nav.Item key={documentReference.referenceDocumentId}>
                <Nav.Link eventKey={`document-references-${index}`}>
                  Document reference {index + 1}
                </Nav.Link>
              </Nav.Item>
            ))}
          <Nav.Item>
            <Nav.Link eventKey="code-template">Code template</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey={`operations`}>Operations</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="parameters">Parameters</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="named-object">
            <NamedObjectControl
              dataPath={dataPath}
              canRename={canRename}
              disabled={disabled}
            />

            <AsyncButton
              id={`${idRoute}-add-document-reference`}
              onClick={handleAddDocumentReference}
              className="d-flex align-items-center btn-sm btn-success m-2"
              disabled={disabled}
            >
              <span className="me-2">Add document reference</span>
              <PlusCircle />
            </AsyncButton>
          </Tab.Pane>

          {documentReferences.map((documentReference, index) => (
            <Tab.Pane
              eventKey={`document-references-${index}`}
              key={`${documentReference.referenceDocumentId}-${index}`}
            >
              <DocumentReferenceControl
                key={`${documentReference.referenceDocumentId}-${index}`}
                dataPath={`${dataPath}/documentRefs/${index}`}
                disabled={disabled}
                siblingCount={documentReferences.length}
              />
            </Tab.Pane>
          ))}

          <Tab.Pane eventKey="code-template">
            {hasCodeTemplate && (
              <AnalysisProgrammingCodeTemplateControl
                dataPath={`${dataPath}/codeTemplate`}
                disabled={disabled}
              />
            )}

            <AsyncButton
              id={`${idRoute}-add-code-template`}
              onClick={handleAddCodeTemplate}
              className="d-flex align-items-center btn-sm btn-success"
              disabled={disabled || hasCodeTemplate}
            >
              <span className="me-2">Add code template</span>
              <PlusCircle />
            </AsyncButton>
          </Tab.Pane>

          <Tab.Pane eventKey="parameters">
            <AnalysisMethodParametersControl
              dataPath={`${dataPath}/codeTemplate`}
              disabled={disabled}
            />
          </Tab.Pane>

          <Tab.Pane eventKey={`operations`}>
            <AnalysisMethodOperationsControl
              dataPath={dataPath}
              disabled={disabled}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

AnalysisMethodControl.displayName = "AnalysisMethodControl";
